import { useCallback } from 'react';
import { Marker, Polygon } from 'react-leaflet';
import { DivIcon, LeafletEvent, Marker as LeafletMarker } from 'leaflet';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { updateShape } from '../../store/slices/missionPlannerSlice';
import { PlannerShape } from '../../types/missionPlanner';
import { calculateBearing } from '../../utils/coordinates';
import {
  RESIZE_HANDLES,
  ShapeHandleAxis,
  fromShapeFrame,
  handlePosition,
  isRotatable,
  resizeShapeTo,
  shapeFrameCorners,
  shapeHalfExtents,
} from '../../utils/geometry';

/**
 * Handles are told apart by shape and cursor, not by colour alone: corners are
 * squares, edges are bars, and the two gesture handles are round. Colour only
 * reinforces it.
 */
const handleIcon = (
  html: string,
  label: string,
  cursor: string,
  size: [number, number]
) =>
  new DivIcon({
    html: `<div role="img" aria-label="${label}" title="${label}"
      style="cursor:${cursor};${html}"></div>`,
    className: 'planner-shape-handle',
    iconSize: size,
    iconAnchor: [size[0] / 2, size[1] / 2],
  });

/** The rotate handle is round and carries a glyph; resize handles never do. */
const gestureIcon = (color: string, glyph: string, label: string, cursor: string) =>
  new DivIcon({
    html: `<div role="img" aria-label="${label}" title="${label}" style="
      width:18px;height:18px;border-radius:50%;background:${color};
      border:2px solid #ffffff;box-shadow:0 1px 3px rgba(0,0,0,0.5);
      color:#ffffff;font-size:11px;line-height:14px;text-align:center;
      font-weight:700;cursor:${cursor};">${glyph}</div>`,
    className: 'planner-shape-handle',
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });

const ROTATE_ICON = gestureIcon('#7b1fa2', '↻', 'Rotate handle', 'grab');

/**
 * Corner/edge names are given in the shape's *own* frame — the "top" edge of a
 * rotated square is the one a viewer reads as its top, which is what an
 * operator points at.
 */
const AXIS_NAME: Record<number, { major: string; minor: string }> = {
  1: { major: 'top', minor: 'right' },
  [-1]: { major: 'bottom', minor: 'left' },
};

const handleName = (axis: ShapeHandleAxis): string => {
  if (axis.major !== 0 && axis.minor !== 0) {
    return `${AXIS_NAME[axis.major].major}-${AXIS_NAME[axis.minor].minor} corner`;
  }
  return axis.major !== 0
    ? `${AXIS_NAME[axis.major].major} edge`
    : `${AXIS_NAME[axis.minor].minor} edge`;
};

/** Presentation for one resize handle, built once — never mid-gesture. */
interface ResizeHandleView {
  axis: ShapeHandleAxis;
  label: string;
  icon: DivIcon;
}

const RESIZE_VIEWS: ResizeHandleView[] = RESIZE_HANDLES.map((axis) => {
  const label = `Resize ${handleName(axis)}`;
  const corner = axis.major !== 0 && axis.minor !== 0;
  // Bars lie along the edge they grab; a rotated shape keeps the unrotated
  // orientation, which still separates "edge" from "corner" at a glance.
  const size: [number, number] = corner
    ? [12, 12]
    : axis.major !== 0
      ? [16, 8]
      : [8, 16];
  const cursor = corner
    ? axis.major === axis.minor
      ? 'nesw-resize'
      : 'nwse-resize'
    : axis.major !== 0
      ? 'ns-resize'
      : 'ew-resize';
  const style =
    `width:${size[0]}px;height:${size[1]}px;background:#f57f17;` +
    `border:2px solid #ffffff;border-radius:${corner ? 2 : 4}px;` +
    `box-shadow:0 1px 3px rgba(0,0,0,0.5);`;
  return { axis, label, icon: handleIcon(style, label, cursor, size) };
});

/**
 * Editing overlay for the selected shape: a dashed bounding box, eight resize
 * handles and a rotate handle.
 *
 * Dragging a corner resizes both axes, an edge only its own, and in both cases
 * the opposite corner/edge stays anchored so the shape grows from the side
 * being pulled (#435). Circles and pentagons stay regular — they average the
 * axes back together rather than offering a different interaction model.
 *
 * There is deliberately no centre handle: the body itself is draggable and
 * already moves live (#346), and a handle pinned to the centre only sat on top
 * of the shape's own click target (#438).
 *
 * Every handle streams its change on `drag`, so the outline follows the pointer
 * instead of jumping on release; the slice coalesces the stream into a single
 * undo entry.
 */
export const ShapeEditor: React.FC = () => {
  const dispatch = useDispatch();
  const shape = useSelector((state: RootState) =>
    state.missionPlanner.draft.shapes.find(
      (s) => s.id === state.missionPlanner.selectedShapeId
    )
  );

  const dragLatLng = (e: LeafletEvent) =>
    (e.target as LeafletMarker).getLatLng();

  const commitRotate = useCallback(
    (e: LeafletEvent) => {
      if (!shape) return;
      const { lat, lng } = dragLatLng(e);
      const rotationDeg = calculateBearing(
        shape.center.lat,
        shape.center.lng,
        lat,
        lng
      );
      dispatch(updateShape({ id: shape.id, changes: { rotationDeg } }));
    },
    [dispatch, shape]
  );

  if (!shape) {
    return null;
  }

  return (
    <>
      {/* The editing frame — non-interactive, and drawn through the corner
          handles themselves so it rotates with the shape. A world-aligned
          rectangle left every corner handle floating off it once the shape was
          turned (#442). */}
      <Polygon
        positions={shapeFrameCorners(shape)}
        color="#f57f17"
        weight={1}
        dashArray="4, 4"
        fill={false}
        interactive={false}
        className="planner-shape-bbox"
      />

      {RESIZE_VIEWS.map((view) => (
        <ResizeHandle
          key={view.label}
          shape={shape}
          view={view}
          onResize={(changes) =>
            dispatch(updateShape({ id: shape.id, changes }))
          }
        />
      ))}

      {/* Rotate handle — a circle is invariant under rotation, so it has none. */}
      {isRotatable(shape.kind) && (
        <Marker
          position={fromShapeFrame(shape, {
            major: shapeHalfExtents(shape).major * 1.6,
            minor: 0,
          })}
          icon={ROTATE_ICON}
          draggable
          eventHandlers={{ drag: commitRotate, dragend: commitRotate }}
        />
      )}
    </>
  );
};

interface ResizeHandleProps {
  shape: PlannerShape;
  view: ResizeHandleView;
  onResize: (changes: Partial<PlannerShape>) => void;
}

/** One corner or edge handle, anchored to the opposite side while dragged. */
const ResizeHandle: React.FC<ResizeHandleProps> = ({
  shape,
  view,
  onResize,
}) => {
  const commit = useCallback(
    (e: LeafletEvent) => {
      const { lat, lng } = (e.target as LeafletMarker).getLatLng();
      onResize(resizeShapeTo(shape, view.axis, lat, lng));
    },
    [onResize, shape, view.axis]
  );

  return (
    <Marker
      position={handlePosition(shape, view.axis)}
      icon={view.icon}
      draggable
      eventHandlers={{ drag: commit, dragend: commit }}
    />
  );
};
